/**
 * The full-width surface a governed document is read on.
 *
 * Not a third column. The analyst layout is a two-column grid with a fixed
 * trace panel, and a document squeezed into 330px is not a document -- so this
 * takes the page and the conversation is one Escape away. It stays inside the
 * app rather than opening a tab: a revision is a conversation, and a document in
 * another window cannot show the change the user just asked for.
 *
 * Every control here is backed by an endpoint. Export renders server-side from
 * the version being read, so a document that has been issued can still be
 * downloaded exactly as it was sent.
 */
import {
  AlertTriangle,
  Check,
  Download,
  FileText,
  History,
  Presentation,
  Send,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  artifactsApi,
  type ExportFormat,
  type ExportMode,
} from "../services/api";
import type { ArtifactComposedEvent } from "../services/agui";
import { captureCharts } from "../services/chartRegistry";
import type { ArtifactVersionSummary } from "../types";
import {
  artifactEmailDocument,
  EmailButton,
  pdfAttachment,
  sectionPanelId,
  type EmailDraftSource,
} from "../features/email";
import { ArtifactDocument } from "./ArtifactDocument";
import { ExportAudienceChooser } from "./DashboardView";

interface ArtifactViewProps {
  event: ArtifactComposedEvent;
  onClose: () => void;
}

export function ArtifactView({ event, onClose }: ArtifactViewProps) {
  const [versions, setVersions] = useState<ArtifactVersionSummary[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [published, setPublished] = useState(event.status === "published");
  const [busy, setBusy] = useState(false);
  // Carries its tone, because the same strip reports a publish confirmation
  // and a failed export, and the shared style is the error one.
  const [notice, setNotice] = useState<{
    tone: "success" | "warning";
    text: string;
  } | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | "">("");
  const [choosingFor, setChoosingFor] = useState<ExportFormat | "">("");

  // Only chart sections were persisted as panels, and only those have a
  // rendered chart the PDF can embed.
  const panelIds = event.spec.sections
    .map((section) => ("panelId" in section ? section.panelId : undefined))
    .filter((id): id is string => Boolean(id));

  // Escape closes it, which is the shortcut anyone expects from something that
  // took over the page.
  useEffect(() => {
    function onKey(keyEvent: KeyboardEvent) {
      if (keyEvent.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadVersions = useCallback(async () => {
    if (!event.artifactId) return;
    try {
      setVersions(await artifactsApi.versions(event.artifactId));
    } catch {
      // The document is readable without its history, so this must not replace
      // it with an error.
      setVersions([]);
    }
  }, [event.artifactId]);

  function toggleHistory() {
    setHistoryOpen((open) => {
      if (!open && versions === null) void loadVersions();
      return !open;
    });
  }

  async function publish() {
    if (!event.artifactId || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await artifactsApi.publish(event.artifactId, event.version);
      setPublished(true);
      setNotice({
        tone: "success",
        text: `Version ${event.version} is published to your workspace and can be cited.`,
      });
      if (historyOpen) void loadVersions();
    } catch (cause: unknown) {
      setNotice({
        tone: "warning",
        text:
          cause instanceof ApiError
            ? cause.message
            : "That version could not be published.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function exportAs(format: ExportFormat, mode: ExportMode) {
    setChoosingFor("");
    setExporting(format);
    setNotice(null);
    try {
      // Only the PDF embeds screen images; the deck builds native PowerPoint
      // charts server-side from the same rows so a recipient can edit them.
      const images = format === "pdf" ? captureCharts(panelIds) : [];
      const blob = await artifactsApi.export(
        event.artifactId,
        format,
        mode,
        images,
        // The version on screen, not the latest: an issued document must
        // export as it was sent.
        event.version,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${event.title || "document"}.${format}`;
      link.click();
      requestAnimationFrame(() => URL.revokeObjectURL(url));
    } catch (cause: unknown) {
      setNotice({
        tone: "warning",
        text:
          cause instanceof ApiError
            ? cause.message
            : "The export could not be produced.",
      });
    } finally {
      setExporting("");
    }
  }

  function prepareEmail(): EmailDraftSource {
    const images = captureCharts(event.spec.sections.map(sectionPanelId));
    return {
      document: artifactEmailDocument({ event, published, images }),
      attachment: async () =>
        pdfAttachment(
          await artifactsApi.export(
            event.artifactId,
            "pdf",
            "executive",
            images.filter((image) => panelIds.includes(image.panelId)),
            event.version,
          ),
          event.title || "document",
        ),
    };
  }

  return (
    <div className="artifact-surface" role="dialog" aria-label={event.title}>
      <header className="artifact-toolbar">
        <div className="artifact-identity">
          <strong>{event.title}</strong>
          <span className="artifact-badges">
            <span className="artifact-badge">v{event.version}</span>
            <span className={`artifact-badge ${published ? "issued" : "draft"}`}>
              {published ? "Published" : "Draft"}
            </span>
            {event.revisionOf && (
              <span className="artifact-badge">Revision</span>
            )}
          </span>
        </div>
        <div className="artifact-actions">
          {/* First, and ahead of the exports, because it is the decision the
              other controls depend on: a draft is a working copy, and what
              gets sent should be a version that was deliberately published. */}
          <button
            type="button"
            className="button secondary"
            onClick={publish}
            disabled={busy || published || !event.saved || !event.artifactId}
            title={
              event.saved
                ? "Publish this version to your workspace so it can be cited"
                : "A document that was not saved cannot be published"
            }
          >
            {published ? <Check size={15} /> : <Send size={15} />}
            {published ? "Published" : "Publish to my workspace"}
          </button>
          {/* Reuses the dashboard toolbar so the chooser has the same
              positioning context it was styled against. */}
          <div className="dashboard-toolbar">
            <button
              type="button"
              className="button tertiary"
              onClick={() => setChoosingFor("pdf")}
              disabled={Boolean(exporting) || !event.saved || !event.artifactId}
              aria-haspopup="menu"
              aria-expanded={choosingFor === "pdf"}
              title={
                event.saved
                  ? "Download this version as a PDF"
                  : "A document that was not saved cannot be exported"
              }
            >
              {exporting === "pdf" ? (
                <Download size={15} className="spinning" />
              ) : (
                <FileText size={15} />
              )}
              PDF
            </button>
            <button
              type="button"
              className="button tertiary"
              onClick={() => setChoosingFor("pptx")}
              disabled={Boolean(exporting) || !event.saved || !event.artifactId}
              aria-haspopup="menu"
              aria-expanded={choosingFor === "pptx"}
              title={
                event.saved
                  ? "Download this version as a deck"
                  : "A document that was not saved cannot be exported"
              }
            >
              {exporting === "pptx" ? (
                <Download size={15} className="spinning" />
              ) : (
                <Presentation size={15} />
              )}
              PowerPoint
            </button>
            {/* Behind Publish for the same reason the exports sit after it:
                what is emailed should be a version that was issued. */}
            <EmailButton
              traceId={event.artifactId || event.title}
              kind="document"
              prepare={prepareEmail}
              disabled={Boolean(exporting) || !published || !event.artifactId}
              disabledReason="Publish this version before emailing it"
            />
            {choosingFor ? (
              <ExportAudienceChooser
                format={choosingFor}
                onChoose={(mode) => void exportAs(choosingFor, mode)}
                onCancel={() => setChoosingFor("")}
              />
            ) : null}
          </div>
          <button
            type="button"
            className="button tertiary"
            onClick={toggleHistory}
            aria-expanded={historyOpen}
            disabled={!event.artifactId}
          >
            <History size={15} />
            History
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close the document"
          >
            <X size={18} />
          </button>
        </div>
      </header>

      {!event.saved && (
        <div className="inline-notice warning" role="alert">
          <AlertTriangle size={17} aria-hidden="true" />
          <span>
            {event.saveError ??
              "This document was composed but could not be saved, so it cannot be issued or revised."}
          </span>
        </div>
      )}
      {notice && (
        <div
          className={`inline-notice ${notice.tone}`}
          role={notice.tone === "warning" ? "alert" : "status"}
        >
          <span>{notice.text}</span>
        </div>
      )}

      {historyOpen && (
        <div className="artifact-history">
          {versions === null ? (
            <p>Loading the history…</p>
          ) : versions.length === 0 ? (
            <p>No earlier versions.</p>
          ) : (
            <ol>
              {versions.map((entry) => (
                <li key={entry.version}>
                  <strong>v{entry.version}</strong>
                  <span>{entry.status === "published" ? "Issued" : "Draft"}</span>
                  <span>{entry.sectionCount} sections</span>
                  {/* Why this version exists. Without it a history is a list of
                      numbers nobody can act on. */}
                  {entry.revisionNote && <em>{entry.revisionNote}</em>}
                  <time>{entry.createdAt.slice(0, 10)}</time>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <div className="artifact-scroll">
        <ArtifactDocument event={event} />
      </div>
    </div>
  );
}

export default ArtifactView;
